import { mockDrivers, mockDriverTasks } from '@/mocks/drivers';
import type {
  CreateDriverPayload,
  Driver,
  DriverDocument,
  DriverDocuments,
  DriverProfilePayload,
  DriverTask,
} from '@/types';
import { delay, shouldSimulateError } from './simulation';

/**
 * In-memory working copy so create / edit / disable / delete behave in the
 * preview. A clearly replaceable demo layer — nothing touches a backend, no
 * password is ever stored, and no document is persisted.
 */
function cloneDocument(doc: DriverDocument | null): DriverDocument | null {
  return doc ? { ...doc } : null;
}

function cloneDocuments(docs: DriverDocuments): DriverDocuments {
  return {
    nationalId: cloneDocument(docs.nationalId),
    license: cloneDocument(docs.license),
    vehicle: cloneDocument(docs.vehicle),
  };
}

function cloneDriver(driver: Driver): Driver {
  return { ...driver, documents: cloneDocuments(driver.documents) };
}

let store: Driver[] = mockDrivers.map(cloneDriver);
const taskStore: DriverTask[] = mockDriverTasks.map((task) => ({ ...task }));
let sequence = 0;

export async function fetchDrivers(): Promise<Driver[]> {
  await delay();
  if (shouldSimulateError('drivers')) {
    throw new Error('failed-to-load-drivers');
  }
  return store.map(cloneDriver);
}

export async function fetchDriver(id: string): Promise<Driver> {
  await delay(500);
  if (shouldSimulateError('drivers')) {
    throw new Error('failed-to-load-drivers');
  }
  const driver = store.find((d) => d.id === id);
  if (!driver) {
    throw new Error('driver-not-found');
  }
  return cloneDriver(driver);
}

export async function fetchDriverTasks(driverId: string): Promise<DriverTask[]> {
  await delay(400);
  if (shouldSimulateError('tasks')) {
    throw new Error('failed-to-load-tasks');
  }
  return taskStore.filter((task) => task.driverId === driverId).map((task) => ({ ...task }));
}

export async function createDriver(payload: CreateDriverPayload): Promise<Driver> {
  await delay();
  const username = payload.username.trim().toLowerCase();
  // Simulated unique-username rule from the auth service.
  if (store.some((d) => d.username.toLowerCase() === username)) {
    throw new Error('username-taken');
  }
  sequence += 1;
  const driver: Driver = {
    id: `DRV-${Date.now().toString().slice(-5)}${sequence}`,
    fullName: payload.fullName.trim(),
    phone: payload.phone.trim(),
    username: payload.username.trim(),
    avatarUrl: payload.avatarUrl,
    availability: 'unavailable',
    accountDisabled: false,
    documents: cloneDocuments(payload.documents),
  };
  store = [driver, ...store];
  // The password is intentionally discarded — never stored anywhere.
  return cloneDriver(driver);
}

export async function updateDriver(id: string, payload: DriverProfilePayload): Promise<Driver> {
  await delay();
  store = store.map((driver) =>
    driver.id === id
      ? {
          ...driver,
          fullName: payload.fullName.trim(),
          phone: payload.phone.trim(),
          avatarUrl: payload.avatarUrl,
          documents: cloneDocuments(payload.documents),
        }
      : driver,
  );
  const updated = store.find((driver) => driver.id === id);
  if (!updated) {
    throw new Error('driver-not-found');
  }
  return cloneDriver(updated);
}

/**
 * The laundry can set a new password without knowing the old one.
 * Nothing is stored — this only simulates the operation.
 */
export async function resetDriverPassword(id: string, _password: string): Promise<void> {
  await delay(700);
  const exists = store.some((driver) => driver.id === id);
  if (!exists) {
    throw new Error('driver-not-found');
  }
}

export async function setDriverDisabled(id: string, disabled: boolean): Promise<void> {
  await delay(500);
  store = store.map((driver) =>
    driver.id === id ? { ...driver, accountDisabled: disabled } : driver,
  );
}

/** Update a driver's availability (e.g. mark busy after a mandatory assignment). */
export async function setDriverAvailability(
  id: string,
  availability: Driver['availability'],
): Promise<void> {
  await delay(400);
  store = store.map((driver) => (driver.id === id ? { ...driver, availability } : driver));
}

/** List the sub-drivers currently available for assignment. */
export async function fetchAvailableDrivers(): Promise<Driver[]> {
  await delay(500);
  if (shouldSimulateError('drivers')) {
    throw new Error('failed-to-load-drivers');
  }
  return store
    .filter((driver) => !driver.accountDisabled && driver.availability === 'available')
    .map(cloneDriver);
}

export async function deleteDriver(id: string): Promise<void> {
  await delay(500);
  store = store.filter((driver) => driver.id !== id);
}